import { useEffect } from 'react';
import { useLangNavigate, useLocalized, useT } from '@/i18n';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, Clock, AlertCircle, RotateCcw, Home, Star, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { AgencyLinks } from '@/components/layout/AgencyLinks';
import { MobileContainer } from '@/components/layout/MobileContainer';
import { SpeechBubble } from '@/components/alisa/SpeechBubble';
import { useGameStore } from '@/store/useGameStore';
import { getModule } from '@/data/moduleData';

export default function ResultsScreen() {
  const { attemptId } = useParams();
  const navigate = useLangNavigate();
  const t = useT();
  const moduleProgress = useGameStore((s) => s.moduleProgress);
  const currentAttempt = useGameStore((s) => s.currentAttempt);
  const startModule = useGameStore((s) => s.startModule);

  const moduleId = attemptId || 'module_1';
  const module = useLocalized(getModule(moduleId));
  const progress = moduleProgress[moduleId];
  const lastAttempt = progress?.attempts?.[progress.attempts.length - 1];
  const hasData = !!(lastAttempt || currentAttempt?.startTime);

  useEffect(() => {
    if (!hasData) {
      navigate('/home', { replace: true });
    }
  }, [hasData, navigate]);

  if (!hasData) {
    return null;
  }

  const errors = lastAttempt?.errors ?? currentAttempt?.errors ?? 0;
  const timeSeconds = lastAttempt?.timeSeconds ?? 0;
  const scores = lastAttempt?.scores ?? currentAttempt?.scores ?? {};

  const getStatus = () => {
    if (errors === 0) return { label: t('ui.results.statusExcellent'), icon: Star, color: 'text-secondary', bgColor: 'bg-secondary/10', borderColor: 'border-secondary/30' };
    if (errors === 1) return { label: t('ui.results.statusGood'), icon: CheckCircle2, color: 'text-success', bgColor: 'bg-success/10', borderColor: 'border-success/30' };
    if (errors === 2) return { label: t('ui.results.statusFair'), icon: AlertTriangle, color: 'text-warning', bgColor: 'bg-warning/10', borderColor: 'border-warning/30' };
    return { label: t('ui.results.statusMoreTraining'), icon: XCircle, color: 'text-destructive', bgColor: 'bg-destructive/10', borderColor: 'border-destructive/30' };
  };

  const status = getStatus();
  const StatusIcon = status.icon;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getRecommendation = () => {
    if (errors === 0) return t('ui.results.recPerfect');
    if (errors <= 2) return t('ui.results.recDecent');
    return t('ui.results.recRetake');
  };

  const handleReplay = () => {
    startModule(moduleId);
    navigate(`/module/${moduleId}`, { replace: true });
  };

  // Calculate total score
  const totalScore = Object.values(scores).reduce((a, b) => a + b, 0);
  const maxPossibleScore = 100;

  return (
    <MobileContainer noPadding>
      <div className="flex-1 flex flex-col overflow-y-auto scrollbar-hide">
        {/* Header with status */}
        <div className="gradient-hero px-5 pt-8 pb-10 rounded-b-[32px] relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.2, stiffness: 200 }}
              className={`w-16 h-16 rounded-full ${status.bgColor} border-2 ${status.borderColor} flex items-center justify-center mb-3`}
            >
              <StatusIcon size={28} className={status.color} />
            </motion.div>
            <h1 className="text-2xl font-extrabold font-heading text-primary-foreground mb-1">
              {status.label}
            </h1>
            <p className="text-sm text-primary-foreground/70 font-body">
              {t('ui.results.moduleComplete', { title: module?.title })}
            </p>
          </motion.div>
        </div>

        {/* Stats cards */}
        <div className="px-5 -mt-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="p-4 shadow-card">
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-1.5">
                    <Clock size={18} className="text-primary" />
                  </div>
                  <p className="text-lg font-bold font-heading text-foreground">{formatTime(timeSeconds)}</p>
                  <p className="text-[10px] text-muted-foreground font-body">{t('ui.results.time')}</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-1.5">
                    <AlertCircle size={18} className="text-destructive" />
                  </div>
                  <p className="text-lg font-bold font-heading text-foreground">{errors}</p>
                  <p className="text-[10px] text-muted-foreground font-body">{t('ui.results.mistakes')}</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-1.5">
                    <Trophy size={18} className="text-secondary" />
                  </div>
                  <p className="text-lg font-bold font-heading text-foreground">{totalScore}</p>
                  <p className="text-[10px] text-muted-foreground font-body">{t('ui.results.points')}</p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Score breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="px-5 mt-4"
        >
          <h3 className="text-sm font-bold font-heading text-foreground mb-2">{t('ui.results.breakdown')}</h3>
          <Card className="p-4 shadow-soft">
            <div className="flex flex-col gap-2.5">
              {Object.entries(scores).map(([key, value]) => {
                const labels = {
                  Safety: t('ui.results.metric.Safety'),
                  Service: t('ui.results.metric.Service'),
                  Efficiency: t('ui.results.metric.Efficiency'),
                  Professionalism: t('ui.results.metric.Professionalism'),
                  StressResistance: t('ui.results.metric.StressResistance'),
                };
                const maxPerMetric = 20;
                const percent = Math.min((value / maxPerMetric) * 100, 100);
                return (
                  <div key={key}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-muted-foreground font-body">{labels[key] || key}</span>
                      <span className="text-xs font-semibold text-foreground font-heading">{value}</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${percent}%` }}
                        transition={{ delay: 0.7, duration: 0.6, ease: 'easeOut' }}
                        className="h-full rounded-full gradient-primary-btn"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </motion.div>

        {/* Alisa's recommendation */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="px-5 mt-4"
        >
          <h3 className="text-sm font-bold font-heading text-foreground mb-2">{t('ui.results.advice')}</h3>
          <SpeechBubble
            text={getRecommendation()}
            emotion={errors === 0 ? 'excited' : errors <= 2 ? 'approval' : 'serious'}
          />
        </motion.div>

        {/* Action buttons */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="px-5 mt-6 pb-8 flex flex-col gap-3"
        >
          <button
            onClick={handleReplay}
            className="w-full gradient-primary-btn text-primary-foreground font-heading font-bold text-base py-3.5 rounded-xl shadow-primary hover:shadow-primary-hover active:scale-[0.98] transition-transform duration-150 uppercase tracking-wide flex items-center justify-center gap-2"
          >
            <RotateCcw size={18} />
            {t('ui.results.tryAgain')}
          </button>
          <Button
            variant="outline"
            className="w-full h-12 rounded-xl font-heading font-semibold text-sm border-2 border-border hover:bg-muted"
            onClick={() => navigate('/home')}
          >
            <Home size={16} className="mr-2" />
            {t('ui.results.backToMenu')}
          </Button>
          <AgencyLinks />
        </motion.div>
      </div>
    </MobileContainer>
  );
}
